import type { ReactNode } from 'react';
import { documentText } from 'ionicons/icons';
import { Card, CardContent, CardHeader, CardTitle } from './Card';

/**
 * Port of terms-of-use-content.component.html. Shown on both /disclaimers and
 * /toc-acceptance, so the legal text lives in exactly one place.
 */
export function TermsOfUseContent({ id }: { id?: string }) {
  return (
    <Card id={id} className="scroll-mt-24">
      <CardHeader>
        <CardTitle icon={documentText}>Website Terms of Use</CardTitle>
      </CardHeader>
      <CardContent className="prose-sm max-w-none space-y-4 text-slate-700">
        <Section n="1" title="Introduction and Acceptance">
          <p>
            These Website Terms of Use (these &ldquo;Terms&rdquo;) govern access to and use of the
            Intraday Vision platform (the &ldquo;Site&rdquo;), which is owned and operated by
            Tertius Group, LLC (&ldquo;Tertius,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
            &ldquo;our&rdquo;). By accessing or using the Site, you agree to be bound by these
            Terms. If you do not agree, you may not access or use the Site.
          </p>
        </Section>

        <Section n="2" title="Authorized Access">
          <p>
            Access to this Site is limited to users expressly authorized by Tertius or its clients.
            You may not share your login credentials or permit others to use your account.
            Unauthorized access or use is prohibited and may be subject to civil and criminal
            penalties.
          </p>
        </Section>

        <Section n="3" title="Commercial Use Only">
          <p>
            This Site and its content are provided solely for legitimate business purposes related
            to your commercial relationship with Tertius or its clients. Consumer or personal use
            is not permitted.
          </p>
        </Section>

        <Section n="4" title="Confidential Information">
          <p>
            Information on this Site may be confidential or proprietary. You agree to use it only
            as authorized and to protect it from unauthorized disclosure.
          </p>
        </Section>

        <Section n="5" title="Intellectual Property">
          <p>
            All content, trademarks, service marks, and materials made available through the Site
            are owned by or licensed to Tertius and are protected by applicable law. &ldquo;Intraday
            Vision&rdquo; is a trademark or service mark of Tertius or its licensors. You are
            granted no rights except as necessary to use the Site in accordance with these Terms.
          </p>
        </Section>

        <Section n="6" title="No Warranties">
          <p className="uppercase">
            The Site and all information, data, reports, and materials made available through the
            Site (including &ldquo;Site Data&rdquo;) are provided &ldquo;as is&rdquo; and &ldquo;as
            available.&rdquo;
          </p>
          <p className="uppercase">
            Tertius disclaims all warranties, express or implied, including warranties of
            merchantability, fitness for a particular purpose, non-infringement, accuracy,
            completeness, and availability. Tertius does not warrant that the Site will be
            uninterrupted, error-free, or free of harmful components.
          </p>
        </Section>

        <Section n="7" title="Limitation of Liability">
          <p className="uppercase">
            To the fullest extent permitted by law, Tertius shall not be liable for any indirect,
            incidental, consequential, special, exemplary, or punitive damages arising out of or
            relating to your use of, or inability to use, the Site.
          </p>
          <p className="uppercase">
            Tertius&rsquo;s total liability for any claim arising out of or relating to the Site
            shall not exceed the greater of (i) $1,000 or (ii) the fees paid by your organization
            for access to the Site during the twelve (12) months preceding the claim.
          </p>
          <p className="uppercase">
            Nothing in these Terms excludes liability where such limitation is prohibited by
            applicable law.
          </p>
        </Section>

        <Section n="8" title="Data Limitations and Reliance Disclaimer">
          <ol className="ml-5 list-[lower-alpha] space-y-4">
            <li>
              <span className="font-semibold">Informational Use Only</span>
              <p className="mt-1">
                The information, reports, transaction data, performance metrics, fund activity
                data, and other materials made available through the Site (collectively,
                &ldquo;Site Data&rdquo;) are provided solely for informational and operational
                convenience purposes to authorized commercial users.
              </p>
              <p className="mt-1 uppercase">
                Site Data is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo;
                basis.
              </p>
            </li>
            <li>
              <span className="font-semibold">Source and Nature of Data</span>
              <p className="mt-1">
                Site Data may be obtained from third-party administrators, custodians,
                intermediaries, fund accounting agents, transfer agents, market data providers, or
                other external vendors. Site Data:
              </p>
              <ol className="mt-1 ml-5 list-decimal space-y-1">
                <li>May be preliminary, estimated, unaudited, or subject to revision;</li>
                <li>
                  May be delayed due to vendor transmission timing, reconciliation processes,
                  system processing cycles, or market data latency;
                </li>
                <li>
                  May not reflect pending transactions, corporate actions, pricing updates,
                  accruals, fee adjustments, or other events not yet processed;
                </li>
                <li>May contain errors, omissions, or inaccuracies.</li>
              </ol>
              <p className="mt-1">
                Tertius does not independently verify all third-party data and does not guarantee
                its completeness, accuracy, reliability, or timeliness. Tertius has no obligation
                to update or correct Site Data except as required by applicable law.
              </p>
            </li>
            <li>
              <span className="font-semibold">No Replacement for Official Records</span>
              <p className="mt-1">
                Site Data does not replace and does not supersede official books and records,
                account statements, confirmations, audited financial statements, regulatory
                filings, subscription documents, governing fund documents, or other formal
                communications issued by the applicable entity.
              </p>
              <p className="mt-1">
                In the event of any discrepancy, the official books and records of the applicable
                entity shall control.
              </p>
            </li>
            <li>
              <span className="font-semibold">
                No Reliance for Investment or Compliance Purposes
              </span>
              <p className="mt-1">Site Data should not be relied upon as the sole basis for:</p>
              <ol className="mt-1 ml-5 list-decimal space-y-1">
                <li>Investment decisions;</li>
                <li>Valuation determinations;</li>
                <li>Accounting treatment;</li>
                <li>Tax reporting;</li>
                <li>Regulatory reporting;</li>
                <li>Legal compliance;</li>
                <li>Client reporting.</li>
              </ol>
              <p className="mt-1">
                Authorized users are responsible for independently verifying material information
                before taking action.
              </p>
              <p className="mt-1">
                Nothing in the Site or in Site Data creates a fiduciary relationship, advisory
                relationship, or other duty beyond the limited access rights granted under these
                Terms.
              </p>
            </li>
            <li>
              <span className="font-semibold">Disclaimer of Warranties as to Data</span>
              <p className="mt-1">
                Without limiting Section 6, to the fullest extent permitted by law, Tertius
                disclaims all representations and warranties, express or implied, regarding Site
                Data, including warranties of accuracy, completeness, merchantability, fitness for
                a particular purpose, non-infringement, availability, and uninterrupted access.
              </p>
            </li>
            <li>
              <span className="font-semibold">Limitation of Liability Relating to Site Data</span>
              <p className="mt-1">
                Without limiting Section 7, to the fullest extent permitted by law:
              </p>
              <ol className="mt-1 ml-5 list-decimal space-y-1">
                <li>
                  Tertius shall not be liable in contract, tort (including negligence), strict
                  liability, statute, or otherwise for any indirect, incidental, consequential,
                  special, exemplary, or punitive damages arising out of or relating to Site Data;
                </li>
                <li>
                  Tertius shall not be liable for losses arising from reliance on Site Data, delays
                  in updates, reconciliation differences, third-party vendor errors, system
                  interruptions, or data transmission failures;
                </li>
                <li>
                  Tertius&rsquo;s total aggregate liability arising out of or relating to Site Data
                  shall not exceed the liability cap set forth in Section 7 of these Terms.
                </li>
              </ol>
              <p className="mt-1">
                Nothing in this section limits liability where such limitation is prohibited by
                applicable law.
              </p>
            </li>
          </ol>
        </Section>

        <Section n="9" title="Service Availability">
          <p>
            Access to the Site may be interrupted, suspended, or limited from time to time due to
            maintenance, upgrades, system failures, network issues, failures or delays of
            third-party service providers, or other causes. The Site is provided on an &ldquo;AS
            IS&rdquo; and &ldquo;AS AVAILABLE&rdquo; basis, and Tertius does not guarantee that the
            Site will be available at any particular time or free from errors, delays, or
            interruptions.
          </p>
          <p>
            The Site relies on data, systems, and services provided by third parties. Tertius is
            not responsible for the availability, accuracy, timeliness, or completeness of such
            third-party services or data.
          </p>
          <p>
            To the fullest extent permitted by law, Tertius shall not be liable for any
            unavailability, interruption, delay, degradation in performance, or inability to access
            or use the Site or Site Data, except as expressly provided in a separate written
            agreement, if any.
          </p>
        </Section>

        <Section n="10" title="Relationship to Other Agreements">
          <p>
            If you or your organization has entered into a separate written agreement with Tertius
            governing access to or use of the Site or related services, that agreement will control
            in the event of any conflict with these Terms; provided, however, that the disclaimers
            and limitations relating to Site Data set forth in Section 8 shall apply to all use of
            Site Data unless expressly overridden in such agreement.
          </p>
        </Section>

        <Section n="11" title="Termination">
          <p>
            Tertius may suspend or terminate your access at any time, with or without notice, for
            any reason.
          </p>
        </Section>

        <Section n="12" title="Modification">
          <p>
            Tertius reserves the right to modify these Terms at any time. Material changes will be
            effective upon posting or upon notice to authorized users. Continued use of the Site
            following such changes constitutes acceptance of the revised Terms.
          </p>
        </Section>

        <Section n="13" title="Governing Law">
          <p>
            These Terms are governed by the laws of the State of Delaware, without regard to its
            conflict-of-laws principles.
          </p>
        </Section>

        <Section n="14" title="Contact">
          <p>For questions about these Terms, please contact us through the Site.</p>
        </Section>

        <p className="pt-2 text-sm text-gray1">Last updated: February 13, 2026</p>
      </CardContent>
    </Card>
  );
}

function Section({ n, title, children }: { n: string; title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-base font-semibold text-slate-900">
        {n}. {title}
      </h3>
      {children}
    </section>
  );
}
